<script setup lang="ts">
import { defineProps, defineEmits, computed } from 'vue';
import type { MetaSlideEmits as Emits, MetaSlideProps as Props } from '@/components/slides/slideTypes';
import { useApiStore } from '@/stores/api';
import BaseSlide from '@/components/slides/BaseSlide.vue';
import MetaUrls from '@/components/meta/MetaUrls.vue';
import MetaTags from '@/components/meta/MetaTags.vue';
import MetaTagAdd from '@/components/meta/MetaTagAdd.vue';
import MetaRating from '@/components/meta/MetaRating.vue';
import { hasTagsCheck } from '@/components/meta/helper';

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const apiConfig = useApiStore();
const hasTags = computed(() => hasTagsCheck(props.meta, apiConfig.tagReadServiceId));
</script>

<template>
  <BaseSlide
    :is-mobile="props.isMobile"
    class="base-slide"
  >
    <div
      v-if="!meta"
      class="meta-pending"
    >
      <span
        class="meta-pending-text"
      >
        Metadata pending.
      </span>
    </div>

    <template
      v-else
    >
      <h1>
        file_id:{{ meta.file_id }}
      </h1>

      <div
        class="section"
      >
        <MetaRating
          v-if="activeRatings"
          :active-ratings="activeRatings"
          @rate="(key, newRating) => emit('rate', key, newRating)"
        />
      </div>

      <div
        class="section"
      >
        <MetaUrls
          v-if="meta.known_urls"
          :urls="meta.known_urls"
          :source="source"
        />
        <div
          v-else
          class="not-loaded"
        >
          No URLs.
        </div>
      </div>

      <div
        class="section"
      >
        <MetaTagAdd
          v-if="hasTags"
          @add="(tag) => emit('add', tag)"
        />
        <MetaTags
          v-if="hasTags"
          :meta="meta"
          @add="(tag) => emit('add', tag)"
          @delete="(tag) => emit('delete', tag)"
        />
        <MetaTagAdd
          v-if="hasTags"
          @add="(tag) => emit('add', tag)"
        />
        <div
          v-else
          class="not-loaded"
        >
          No tags loaded.
        </div>
      </div>
    </template>
  </BaseSlide>
</template>

<style scoped>
.section {
  padding-top: 1em;
  border-top: 1px solid #393938;
  margin-top: 1em;
}
:deep(ul li.tag) {
  float: left;
  padding-right: 2em;
  display: inline;
}

.meta-pending {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: var(--text-secondary, #888);
}

.meta-pending-text {
  font-size: 0.85rem;
  opacity: 0.7;
}

.not-loaded {
  font-size: 0.8rem;
  opacity: 0.5;
}
</style>
